import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { cn } from "./cn";

/**
 * A small anchored panel that closes on outside click or Escape.
 *
 * Deliberately not a modal: these are attachment-menu sized, they never take
 * the whole screen, and the composer behind them stays visible so an agent can
 * see the reply they are inserting into.
 *
 * Positioned with plain absolute layout rather than a floating-ui style
 * library. That began as a fair trade -- the triggers all lived in a toolbar at
 * the bottom of a panel of known width, so "above and left-aligned,
 * right-aligned near the edge" covered every real case. It stopped being true
 * once these appeared in page headers and table cells, where the trigger can be
 * anywhere: `align="right"` anchors the panel's right edge to the trigger's, so
 * a trigger near the left edge throws a 16rem menu off the side of the screen.
 * On a 320-wide phone the company page's Log activity menu started at -146px --
 * more than half of it unreachable, with nothing on screen to say so.
 *
 * So the panel now checks where it actually landed and slides itself back on.
 * See `nudge`. Still no dependency, and still nothing measured on the happy
 * path beyond one layout read per open.
 */
export function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  label,
  align = "left",
  side = "top",
  overlay = false,
  className,
  triggerClassName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  label: string;
  align?: "left" | "right";
  /**
   * Which way it opens. Upwards by default, which is right for the composer
   * toolbar these were built for; a trigger in a page header needs the other.
   */
  side?: "top" | "bottom";
  /**
   * Position against the viewport instead of the trigger's own box.
   *
   * For triggers inside something that scrolls. An absolutely positioned panel
   * is clipped by any ancestor with `overflow`, so a dropdown in a table cell
   * gets cut off at the edge of the table -- which reads as "only two of the
   * six options exist" rather than as a layout bug. Fixed positioning escapes
   * the clip; the cost is having to measure, which is why it is not the
   * default for the composer toolbars that do not need it.
   */
  overlay?: boolean;
  className?: string;
  /**
   * Extra classes for the trigger button and the box around it.
   *
   * For a caller laying its triggers out itself -- a grid of equal-width
   * actions needs this one to fill its cell like the plain buttons beside it,
   * and the wrapper is in the way of that as much as the button is.
   */
  triggerClassName?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const anchor = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<React.CSSProperties | null>(null);
  /** Horizontal correction, in pixels, when the panel opened off-screen. */
  const [nudge, setNudge] = useState(0);

  /** Where the panel goes, measured from the trigger and the viewport. */
  const place = useCallback((): void => {
    const trigger = anchor.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const below = window.innerHeight - rect.bottom - gap - margin;
    const above = rect.top - gap - margin;
    /* Open whichever way has room, preferring the side that was asked for. */
    const openDown = side === "bottom" ? below > 160 || below >= above : above < 160 && below > above;

    setBox({
      position: "fixed",
      left: align === "right" ? undefined : rect.left,
      right: align === "right" ? window.innerWidth - rect.right : undefined,
      top: openDown ? rect.bottom + gap : undefined,
      bottom: openDown ? undefined : window.innerHeight - rect.top + gap,
      maxHeight: openDown ? below : above,
    });
  }, [align, side]);

  useLayoutEffect(() => {
    if (!open || !overlay) return;
    place();
  }, [open, overlay, place]);

  /*
   * Slide the panel back inside the viewport if it opened outside it.
   *
   * A correction applied after the fact rather than a placement decided in
   * advance, because the width that matters is the panel's own and nothing
   * knows it until it has rendered -- `w-64` here, a content-sized menu there.
   * One extra layout pass per open buys every caller edge-safety without any of
   * them having to think about which way their trigger will fall.
   *
   * Subtracting the current `nudge` recovers where the panel would sit with no
   * correction, so every pass measures the same thing and this settles: once the
   * shift it wants equals the shift already applied, it stops.
   *
   * Runs for both modes. The CSS-positioned one needs it most -- it has no idea
   * where it is -- but a fixed panel anchored to a trigger near an edge
   * overflows in exactly the same way.
   */
  useLayoutEffect(() => {
    if (!open) {
      if (nudge !== 0) setNudge(0);
      return;
    }

    const el = panel.current;
    if (!el) return;

    const margin = 8;
    const rect = el.getBoundingClientRect();
    /* Where it would be sitting if this effect had never run. */
    const left = rect.left - nudge;
    const right = rect.right - nudge;

    let shift = 0;
    if (right > window.innerWidth - margin) {
      shift = window.innerWidth - margin - right;
    }
    /* Left wins when the panel is too wide to satisfy both: an overflowing
       right edge can at least be scrolled to, while anything past the left edge
       is simply gone. */
    if (left + shift < margin) shift = margin - left;

    if (shift !== nudge) setNudge(shift);
  }, [open, box, nudge]);

  useEffect(() => {
    if (!open || !overlay) return;

    /* Capture, so it also fires for the inner container the trigger sits in
       rather than only for the window. */
    const onMove = (): void => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, overlay, place]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      if (!host.current?.contains(event.target as Node)) onOpenChange(false);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // Stopped so the ticket view's own Escape handler does not also fire and
      // close the whole ticket behind the popover.
      event.stopPropagation();
      onOpenChange(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onOpenChange]);

  return (
    <div ref={host} className={cn("relative", triggerClassName)}>
      <button
        ref={anchor}
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        className={cn(
          "flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {open ? (
        <div
          ref={panel}
          role="dialog"
          aria-label={label}
          style={{
            ...(overlay ? (box ?? { visibility: "hidden" }) : null),
            ...(nudge === 0 ? null : { transform: `translateX(${nudge}px)` }),
          }}
          className={cn(
            "z-30 rounded-lg border border-line bg-surface shadow-lg",
            /* Never wider than the screen it is on. The nudge slides a panel
               back inside the viewport; it cannot shrink one that was never
               going to fit. */
            "max-w-[calc(100vw-1rem)]",
            overlay
              ? "overflow-y-auto"
              : cn(
                  "absolute",
                  // Upwards by default: the toolbar sits at the bottom of the
                  // composer, which is itself at the bottom of the screen.
                  side === "top" ? "bottom-full mb-1" : "top-full mt-1",
                  align === "right" ? "right-0" : "left-0",
                ),
            className,
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
